import axios, { AxiosInstance, AxiosResponse } from 'axios';
import store from '@/store';
import { LoginCredentialsType, InfinityQuery } from '@/types';
import { getAuthToken, setAuthToken, getRefreshToken, setRefreshToken,
  deleteRefreshToken, deleteAuthToken } from '@/services/storage';
import { APIURL, baseURL } from '@/core/constants';
import router from '@/router';
import { error } from 'console';

const passErroToComponent = [
  'news',
  'rent-ad-requests',
  'verify-sms-code',
];

const instanceAxios: AxiosInstance = axios.create({
  baseURL: APIURL,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-type': 'application/json;charset=utf-8',
    'x-base-url': baseURL,
  },
});

instanceAxios.defaults.headers.post['Access-Control-Allow-Origin'] = '*';

const exludeAuthorizationPaths: string[] = [
  'settings',
  'accounts/registration-confirm-phone',
  'accounts/registration-confirm-email',
  'accounts/refresh',
];

instanceAxios.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    config.headers['Accept-language'] = store.getters.language;
    const authNeeded = exludeAuthorizationPaths.every((x) => !(config as any).url.includes(x));
    if (token && authNeeded) {
      config.headers.Authorization = 'Bearer ' + token;
    } else {
      config.headers.Authorization = null;
    }

    return config;
  },
  (error) => {
    store.dispatch('showCentralAlert');
    return Promise.reject(error);
  },
);

instanceAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status >= 500) {
      store.dispatch('showCentralAlert');
    } else if (error.config && error.response?.status === 401) {
      const refresh = getRefreshToken();
      if (refresh) {
        const token = updateToken().then(
          (tokenID): any => {
            if (tokenID) {
              error.config.headers.Authorization = `Bearer ${tokenID}`;
              return axios.request(error.config);
            }
          },
        ).catch((e) => {
          deleteAuthToken();
          deleteRefreshToken();
          // MOVE TO login page
          const { href } = router.resolve({name: 'login'});
          location.href = href;
        });
      }
    } else if ((error.response?.status === 400 || error.response?.status > 401) && error.config.method.toLowerCase() === 'get') {
      if (!passErroToComponent.some((path) => error.config.url.includes(path))) {
        store.dispatch('handleAPIError', error.response.data.mobile_errors?.[0]?.messages[0]);
        return error.response;
      }
    }
    return Promise.reject(error);
  },
);

/**
 *
 * @summary Parse object to query string
 * @param {} object
 * @returns {string} query string
 * @example
 *  // return 'end=2019-06-06&location=VDNH&rooms=1%2C3&start=2019-05-06'
 *  objectToQuerystring({end:"2019-06-06",location:"VDNH",rooms:[1,3],start:"2019-05-06"})
 */
export function objectToQuerystring(obj) {
  if (!obj) { return ''; }
  const str = Object
    .keys(obj)
    .filter((key) => (obj[key] instanceof Array ? obj[key].length > 0 : !!obj[key]))
    .map((key) => (encodeURIComponent(key) + '=' + encodeURIComponent(obj[key])))
    .filter((x) => x)
    .join('&');

  return str.indexOf('&') === str.length - 1 ? str.slice(0, -1) : str;
}

export function updateToken() {
  const refresh = getRefreshToken();
  const promise = new Promise((resolve, reject) => {
    instanceAxios
      .post('/accounts/refresh/', { refresh })
      .then((response: AxiosResponse) => {
        setAuthToken(response.data.access);
        getLoggedUser();
        resolve(response.data.access);
      })
      .catch((reason: any) => {
        reject(reason);
      });
  });
  return promise;
}

export function login(credentials: LoginCredentialsType) {
  return instanceAxios
    .post('/accounts/login/', credentials)
    .then(async (response: AxiosResponse) => {
      if (response.status === 206) {
        return response;
      }
      setAuthToken(response.data.access);
      setRefreshToken(response.data.refresh);
      await getLoggedUser();
      return response;
    })
    .catch((error: any) => {
      console.error(error);
      if (error.response.status < 500) {
        return error.response;
      } else {
        return error;
      }
    });
}
export function getLoggedUser() {
  return new Promise((resolve, reject) => {
    return instanceAxios.get('/accounts/me/').then(async (response: AxiosResponse) => {
      if (response.status === 200) {
        return store.dispatch('user/setUser', response.data).then(async () => {
          await store.dispatch('accounts/getGlobalPermissions');
          resolve(true);
        });
      } else {
        reject(false);
      }
    });
  })
  .catch((error: any) => {
    console.error(error);
    return error;
  });
}

export function page(name: string) {
  return instanceAxios.get(`/settings/${name}/`)
    .then((response: AxiosResponse) => {
    return response.data;
  })
  .catch((error: any) => {
    return error.response;
  });
}

export function getSettings() {
  return instanceAxios.get(`/settings/`)
    .then((response: AxiosResponse) => {
    return response.data;
  })
  .catch((error: any) => {
    return error.response;
  });
}

export function getApartments() {
  return instanceAxios.get(`/apartments/related/`,
    { headers: { 'Authorization': null} }).then((response: AxiosResponse) => {
    return response.data;
  })
  .catch((error: any) => {
    return error.response;
  });
}

export function pinApartment(id) {
  const data = {id_apartment : id};
  return instanceAxios.post(`/apartments/pin/`, data )
    .then((response: AxiosResponse) => {
    return response.data;
  })
  .catch((error: any) => {
    return error.response;
  });
}

export function unpinApartment(id) {
  return instanceAxios.post(`/apartments/unpin/`,
    { id_apartment : id }).then((response: AxiosResponse) => {
    return response.data;
  })
  .catch((error: any) => {
    return error.response;
  });
}

export function sendSearchDataToApi(name: string, data) {
  const queryStr = '?' + objectToQuerystring(data);
  return instanceAxios.get(`/rent-ad-requests/${queryStr}`).then((response: AxiosResponse) => {
    return { data: response.data, query: queryStr };
  });
}

export function verifyPhoneByCode(data) {
  return instanceAxios.post('/accounts/registration-confirm-phone/', data)
    .then((response: AxiosResponse) => {
      setAuthToken(response.data.access);
      setRefreshToken(response.data.refresh);
      getLoggedUser();
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function confirmUser(data) {
  return instanceAxios.post('/accounts/login-auth-code/', data)
    .then((response: AxiosResponse) => {
      setAuthToken(response.data.access);
      setRefreshToken(response.data.refresh);
      getLoggedUser();
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function resendLoginVerifyCode(id) {
  return instanceAxios.post('/accounts/login-resend-2fa-code/', id)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function resendRegistrationVerifyCode(id) {
  return instanceAxios.post('/accounts/registration-resend-2fa-code/', id)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function registerUser(data) {
  return instanceAxios.post('/accounts/registration/', data)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function passwordResetRequest(email: string) {
  return instanceAxios.post('/accounts/password-reset-request/', {email})
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function confirmEmail(code: string) {
  return instanceAxios.post('/accounts/registration-confirm-email/', {email_code: code})
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      console.error(error);
      return error.response;
    });
}

export function fetchAboutApartmentById(id) {
  return instanceAxios.get(`/rent-ad-requests/${id}/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function setPassword(data: {}) {
  return instanceAxios.post('/accounts/password-reset-change/', data)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function uploadPhoto(data: {id: 0, photo: {}}) {
  return instanceAxios.patch(`/accounts/${data.id}/upload-photo/`, data.photo)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function uploadData(data: {id: 0, newData: {}}) {
  return instanceAxios.patch(`/accounts/${data.id}/`, data.newData)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function uploadEmail(data: {}) {
  return instanceAxios.patch(`/accounts/change-email/`, data)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function uploadPhone(data: {}) {
  return instanceAxios.patch(`/accounts/change-phone/`, data)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function deleteUser() {
  return instanceAxios.patch(`/accounts/delete/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function sendUpdateUserPhoneVerify(data) {
  return instanceAxios.get(`/accounts/${data.id}/verify-sms-code/${data.sms_code}/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function changePassword(data) {
  return instanceAxios.patch(`/accounts/change-password/`, data)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getNotification(query) {
  return instanceAxios.get(`/notifications/?${objectToQuerystring(query)}`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function notificationIsRead(id) {
  return instanceAxios.get(`/notifications/${id}/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function checkNewNotification() {
  return instanceAxios.get(`/notifications/check-new/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getFeedbackList(query: InfinityQuery) {
  return instanceAxios.get(`/feedback/?${objectToQuerystring(query)}`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function postFeedback(data) {
  return instanceAxios.post(`/feedback/`, data)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getFeedbackItem(id) {
  return instanceAxios.get(`/feedback/${id}/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function sendMessage(id, data) {
  return instanceAxios.post(`/feedback/${id}/comment/`, data)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getAllNews() {
  return instanceAxios.get(`/news/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getPageNews(query) {
  return instanceAxios.get(`/news/${query}`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getNewsItem(id) {
  return instanceAxios.get(`/news/${id}/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getSimilarNewsItem(id) {
  return instanceAxios.get(`/news/${id}/similar-news/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function newAdRequest(data) {
  return axios.all([instanceAxios.post(`/rent-ad-requests/`, data.dataAd),
          instanceAxios.put(`/apartments/${data.apartmentId}/`, {...data.dataApartment, type: 'related'})])
      .then(axios.spread((responseRent: AxiosResponse, responseApartment: AxiosResponse) => {
        return {responseRent, responseApartment};
      }))
      .catch((error: any) => {
        return error.response;
      });
}

export function updateAdRequest(data) {
  return axios.all([instanceAxios.put(`/rent-ad-requests/${data.update}/`, data.dataAd),
          instanceAxios.put(`/apartments/${data.apartmentId}/`, {...data.dataApartment, type: 'related'})])
      .then(axios.spread((responseRent: AxiosResponse, responseApartment: AxiosResponse) => {
        return {responseRent, responseApartment};
      }))
      .catch((error: any) => {
        return error.response;
      });
}

export function getApartment(id) {
  return instanceAxios.get(`/apartments/${id}/?type=related`);
}

export function getTokenForm(url) {
  return instanceAxios.get(`/accounts/get-token-form/?url=${url}`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getCard(order) {
  const url = !!order && !!order.length
    ? `accounts/get-card/?order_number=${order}`
    : '/accounts/get-card/';
  return instanceAxios.get(url)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function deleteCard() {
  return instanceAxios.post('/accounts/delete-card/')
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getPaymentInfo(id) {
  return instanceAxios.get(`rent-ad-requests/${id}/get-payment-info-new/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function getPaymentInfoPay({id, c_order_number, result}: any) {
  const query = objectToQuerystring({c_order_number, result});
  const url = !query
    ? `payments/${id}/get-payment-info/`
    : `payments/${id}/get-payment-info/?${query}`;
  return instanceAxios.get(url)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function apartmentReserve(id) {
  return instanceAxios.get(`rent-ad-requests/${id}/reserve/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function sendBusinessRequests(data) {
  return instanceAxios.post(`/business-requests/`, data)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function rentApartmentAd(data) {
  return instanceAxios.post(`/rent-ad-requests/${data.id}/rent-new/`, data.value)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function rentApartmentComplete(data) {
  return instanceAxios.post(`/rent-ad-requests/${data.adId}/rent-new-complete/`, data.paymentId)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function getPermission() {
  return instanceAxios.get('/accounts/permissions/?flat=true')
      .then((response: AxiosResponse) => {
        return response.data;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function getPayments({name, query}) {
  const url = !!name
    ? `/payments/${name}/?${objectToQuerystring(query)}`
    : `/payments/?${objectToQuerystring(query)}`;
  return instanceAxios.get(url)
      .then((response: AxiosResponse) => {
        return response.data;
      })
      .catch((error: any) => {
        throw new Error(error.response);
      });
}

export function pay(id) {
  return instanceAxios.post(`/payments/${id}/pay/`)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function getNavigation() {
  return instanceAxios.get('/settings/navigation/')
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function addLodger(data) {
  return instanceAxios.patch(`/apartments/${data.id}/add-lodger/`, {email: data.email})
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function deleteLodger(data) {
  return instanceAxios.patch(`/apartments/${data.id}/remove-lodger/${data.lodger}/`)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error;
      });
}

export function verifyLodger(data) {
  return instanceAxios.post(`/apartments/verify-lodger/${data}/`)
      .then((response: AxiosResponse) => {
        return response.status;
      })
      .catch((error: any) => {
        return error.status;
      });
}

export function cancelRent(data) {
  return instanceAxios.patch(`/lease-contracts/${data.id}/close/`, data.value)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function prolongRent(data) {
  return instanceAxios.patch(`/lease-contracts/${data.id}/prolong/`, data.value)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function cancelRentAdRequest(id) {
  return instanceAxios.patch(`/rent-ad-requests/${id}/cancel/`)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function changeTermsRentAdRequest(data) {
  return instanceAxios.patch(`/rent-ad-requests/${data.id}/change-terms/`, data.value)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: any) => {
        return error.response;
      });
}

export function resendConfirmationEmail() {
  return instanceAxios.get(`/accounts/resend-confirmation-email/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}

export function verifyEmail(id, key) {
  return instanceAxios.get(`/accounts/${id}/verify-email/${key}/`)
    .then((response: AxiosResponse) => {
      return response;
    })
    .catch((error: any) => {
      return error.response;
    });
}
